import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import querystring from 'query-string';
import { isSignInWithEmailLink, signInWithEmailLink, updatePassword } from 'firebase/auth';
import CircularProgress from '@material-ui/core/CircularProgress';

import { queryStrKeys } from 'features/login/utils/config';
import {
    auth,
    firebaseApiErrCodes,
    firebaseEmailLinkQueryStrKeys,
    signout
} from 'features/login/utils/firebaseManager';
import { getPasswordPolicyErrMsg } from 'features/login/utils/passwordPolicy';
import { getQueryString } from 'features/login/utils/manipulateUrl';
import { paths } from 'features/login/hooks/useInitAndRedirectApp';

import useHistoryWithCurrentSearchHash from 'features/login/hooks/useHistoryWithCurrentSearchHash';

import BlockPasswordInput from 'features/login/components/pageEmailVerifyLanding/BlockPasswordInput';
import BlockInfoAndConfirmRedirect from 'features/login/components/BlockInfoAndConfirmRedirect';

export default function EmailVerifyLandingSignupAndForgetPassword() {
    const { t } = useTranslation();
    const history = useHistoryWithCurrentSearchHash();
    const [email] = useState(
        () => querystring.parse(window.location.search)[queryStrKeys.VERIFY_EMAIL]
    );
    const [displayType, setDisplayType] = useState('');
    const passwordInputRef = useRef(null);
    // signInWithEmailLink 會消耗網址上一次性的 OOB code，整個流程只能呼叫一次。
    // 密碼被 Firebase 拒絕後使用者會留在同一個表單重試，這時必須沿用已登入的 user，
    // 否則第二次提交會先撞上 auth/invalid-action-code，變成怎麼改都不會成功的死路。
    const signedInUserRef = useRef(null);

    useEffect(() => {
        if (isSignInWithEmailLink(auth, window.location.href)) {
            setDisplayType(displayTypes.PASSWORD);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleConfirmPassword({ password }) {
        try {
            if (!signedInUserRef.current) {
                const credential = await signInWithEmailLink(auth, email, window.location.href);
                signedInUserRef.current = credential.user;
            }
            await updatePassword(signedInUserRef.current, password);
        } catch (error) {
            if (
                error.code === firebaseApiErrCodes.AUTH_PASSWORD_DOES_NOT_MEET_REQUIREMENTS ||
                error.code === firebaseApiErrCodes.AUTH_WEAK_PASSWORD
            ) {
                // 前端驗證理論上已擋下，這裡是前端規則與 Firebase console 設定不同步時的防線。
                // 兩個錯誤碼都要接：Require 模式丟前者，政策關閉時的後端預設 6 碼檢查丟後者。
                passwordInputRef.current.setPasswordErrMsg(getPasswordPolicyErrMsg());
            } else {
                // 任何其他失敗（例如連結過期的 invalid/expired action code）都必須有畫面回饋，
                // 否則使用者按下儲存後會遇到毫無反應的死路。
                passwordInputRef.current.setPasswordErrMsg(i18n.t('label.error.set_pw.failed'));
                console.error('set password', error);
            }
            return;
        }

        // 密碼已經更新成功。以下收尾步驟即使失敗也不能回報成「密碼設定失敗」，
        // 否則使用者會照提示重試一個其實已經完成的動作。
        history.replace({
            search: getQueryString({
                remove: [
                    ...firebaseEmailLinkQueryStrKeys,
                    queryStrKeys.VERIFY_EMAIL_TYPE,
                    queryStrKeys.VERIFY_EMAIL
                ]
            })
        });
        try {
            await signout();
        } catch (error) {
            console.error('signout after set password', error);
        }
        setDisplayType(displayTypes.PASSWORD_CONFIRM);
    }

    if (displayType === displayTypes.PASSWORD) {
        return (
            <BlockPasswordInput
                ref={passwordInputRef}
                email={email}
                onSave={handleConfirmPassword}
            />
        );
    }
    if (displayType === displayTypes.PASSWORD_CONFIRM) {
        return (
            <BlockInfoAndConfirmRedirect
                title={t('label.pw_set_done')}
                desc={t('label.login_again')}
                btnText={t('btn.to_login_page')}
                redirectPath={paths.LOGIN}
            />
        );
    }
    return <CircularProgress />;
}

const displayTypes = {
    PASSWORD: 'PASSWORD',
    PASSWORD_CONFIRM: 'PASSWORD_CONFIRM'
};
